/* Custom Tiptap doc schema with title-as-first-node (Phase 5e §2.2).
 *
 * Replaces Tiptap's default Document so the doc structure is:
 *   doc = title block+
 *
 * Title is a heading-like node that:
 *   - Is always the first child
 *   - Cannot be deleted, dragged, or moved
 *   - Holds inline content only (text + marks like bold/italic/link)
 *   - Has its own placeholder
 *
 * Behavioral rules implemented as keyboard shortcuts on the Title node:
 *   - Enter at end of title → move cursor to body
 *   - Tab in title → move cursor to body (not indent)
 *   - Backspace at start of body → no-op (don't merge into title)
 *   - Title can become empty but not removed
 */
import { Node } from '@tiptap/core';

export const Title = Node.create({
  name: 'title',
  content: 'inline*',
  group: 'block',
  defining: true,
  isolating: true,

  parseHTML() {
    return [
      { tag: 'h1.cs-title' },
      // Allow legacy posts: a leading <h1> in body HTML is treated as the title
      { tag: 'h1', priority: 51 },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['h1', { ...HTMLAttributes, class: 'cs-title' }, 0];
  },

  addKeyboardShortcuts() {
    return {
      // Enter at the end of the title moves cursor into the body
      Enter: ({ editor }) => {
        const { state } = editor;
        const { $from } = state.selection;
        if ($from.parent.type.name !== 'title') return false;
        const atEnd = $from.parentOffset === $from.parent.content.size;
        if (!atEnd) return false;
        const after = $from.after();
        // If there's already a paragraph after the title, jump into it.
        // Otherwise insert one.
        const next = state.doc.nodeAt(after);
        if (next && next.type.name === 'paragraph') {
          editor.chain().focus().setTextSelection(after + 1).run();
        } else {
          editor.chain().focus()
            .insertContentAt(after, { type: 'paragraph' })
            .setTextSelection(after + 1)
            .run();
        }
        return true;
      },

      // Tab in the title jumps to the body (replaces default tab indent)
      Tab: ({ editor }) => {
        const { $from } = editor.state.selection;
        if ($from.parent.type.name !== 'title') return false;
        const after = $from.after();
        const next = editor.state.doc.nodeAt(after);
        if (next) {
          editor.chain().focus().setTextSelection(after + 1).run();
          return true;
        }
        return false;
      },

      // Backspace at start of body — don't merge into title
      Backspace: ({ editor }) => {
        const { $from, empty } = editor.state.selection;
        if (!empty) return false;
        if ($from.parent.type.name === 'title') return false;
        if ($from.parentOffset !== 0) return false;
        // We're at the start of a non-title block; check if the previous
        // block is the title node.
        const before = $from.before();
        if (before <= 0) return false;
        const prev = editor.state.doc.resolve(before - 1).parent;
        if (prev && prev.type.name === 'title') {
          // Block the merge — keep the title intact
          return true;
        }
        return false;
      },
    };
  },
});

export const Document = Node.create({
  name: 'doc',
  topNode: true,
  content: 'title block+',
});

export const TitleBody = [Document, Title];
